import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  BarChart3, Users, BookOpen, Target, TrendingUp, Loader2, Award,
  GraduationCap, ArrowRight, Activity, PieChart
} from 'lucide-react';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRADE_COLORS = { A: 'bg-green-500', B: 'bg-blue-500', C: 'bg-yellow-500', D: 'bg-orange-500', F: 'bg-red-500' };

const EducatorAnalyticsPage = () => {
  const [overview, setOverview] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [courseDetail, setCourseDetail] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [loading, setLoading] = useState(true);
  const [engDays, setEngDays] = useState(30);

  useEffect(() => {
    const init = async () => {
      try {
        const [ov, comp, eng] = await Promise.all([
          axios.get(`${API}/educator-analytics/overview`),
          axios.get(`${API}/educator-analytics/comparison`),
          axios.get(`${API}/educator-analytics/engagement?days=${engDays}`),
        ]);
        setOverview(ov.data);
        setComparison(comp.data);
        setEngagement(eng.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    init();
  }, [engDays]);

  const loadCourseDetail = async (courseId) => {
    setSelectedCourse(courseId);
    try {
      const res = await axios.get(`${API}/educator-analytics/course/${courseId}`);
      setCourseDetail(res.data);
    } catch (err) { console.error(err); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-violet-500" /></div>;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="edu-analytics-page">
      <h1 className="text-3xl sm:text-4xl font-bold text-[var(--isp-heading)] flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-white" />
        </div>
        Educator Analytics
      </h1>

      {/* Overview Cards */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="overview-cards">
          {[
            { label: 'Courses', value: overview.total_courses, icon: BookOpen, color: 'violet' },
            { label: 'Students', value: overview.total_students, icon: Users, color: 'blue' },
            { label: 'Materials', value: overview.total_materials, icon: GraduationCap, color: 'emerald' },
            { label: 'Avg Score', value: `${overview.avg_exam_score}%`, icon: Target, color: 'amber' },
          ].map((c, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
              <Card className="p-4  bg-[var(--isp-glass)] border-[var(--isp-glass-border)]" data-testid={`stat-${c.label.toLowerCase()}`}>
                <c.icon className={`w-5 h-5 text-${c.color}-400 mb-2`} />
                <p className="text-2xl font-bold text-[var(--isp-heading)]">{c.value}</p>
                <p className="text-xs text-[var(--isp-muted)]">{c.label}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Course Comparison */}
      {comparison?.courses?.length > 0 && (
        <Card className="p-5  bg-[var(--isp-glass)] border-[var(--isp-glass-border)]" data-testid="course-comparison">
          <h2 className="text-lg font-semibold text-[var(--isp-heading)] mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-violet-400" /> Course Comparison
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[var(--isp-muted)] text-xs">
                  <th className="pb-2">Course</th>
                  <th className="pb-2 text-center">Students</th>
                  <th className="pb-2 text-center">Materials</th>
                  <th className="pb-2 text-center">Avg Score</th>
                  <th className="pb-2 text-center">Exams</th>
                  <th className="pb-2 text-center">Avg XP</th>
                  <th className="pb-2"></th>
                </tr>
              </thead>
              <tbody>
                {comparison.courses.map((c, i) => (
                  <tr key={c.course_id} className="border-t border-[var(--isp-glass-border)]">
                    <td className="py-2">
                      <p className="font-medium text-[var(--isp-heading)]">{c.course_name}</p>
                      {c.course_code && <p className="text-[10px] text-[var(--isp-muted)]">{c.course_code}</p>}
                    </td>
                    <td className="py-2 text-center text-[var(--isp-body)]">{c.enrolled}</td>
                    <td className="py-2 text-center text-[var(--isp-body)]">{c.materials}</td>
                    <td className="py-2 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${c.avg_exam_score >= 70 ? 'text-green-300 bg-green-500/20' : c.avg_exam_score >= 50 ? 'text-yellow-300 bg-yellow-500/20' : 'text-red-300 bg-red-500/20'}`}>
                        {c.avg_exam_score}%
                      </span>
                    </td>
                    <td className="py-2 text-center text-[var(--isp-body)]">{c.total_exams}</td>
                    <td className="py-2 text-center text-[var(--isp-body)]">{c.avg_student_xp}</td>
                    <td className="py-2">
                      <Button size="sm" variant="ghost" onClick={() => loadCourseDetail(c.course_id)} className="h-6 px-2 text-[10px] text-violet-300" data-testid={`detail-${c.course_id}`}>
                        <ArrowRight className="w-3 h-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Engagement Trends */}
      {engagement?.daily_activity?.length > 0 && (
        <Card className="p-5  bg-[var(--isp-glass)] border-[var(--isp-glass-border)]" data-testid="engagement-chart">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[var(--isp-heading)] flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-400" /> Student Engagement
            </h2>
            <select value={engDays} onChange={e => setEngDays(Number(e.target.value))} className="px-2 py-1 rounded-lg bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)] text-xs text-[var(--isp-body)]">
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
            </select>
          </div>
          <div className="flex items-end gap-1 h-32">
            {engagement.daily_activity.map((d, i) => {
              const maxStudents = Math.max(...engagement.daily_activity.map(x => x.active_students), 1);
              const height = (d.active_students / maxStudents) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center justify-end gap-0.5 group" title={`${d.date}: ${d.active_students} students, ${d.sessions} sessions`}>
                  <span className="text-[8px] text-[var(--isp-muted)] opacity-0 group-hover:opacity-100">{d.active_students}</span>
                  <div className="w-full rounded-t bg-gradient-to-t from-blue-500 to-cyan-400 transition-all" style={{ height: `${Math.max(height, 4)}%` }} />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-[8px] text-[var(--isp-muted)]">{engagement.daily_activity[0]?.date}</span>
            <span className="text-[8px] text-[var(--isp-muted)]">{engagement.daily_activity[engagement.daily_activity.length - 1]?.date}</span>
          </div>
        </Card>
      )}

      {/* Course Detail */}
      {courseDetail && (
        <Card className="p-5  bg-[var(--isp-glass)] border-[var(--isp-glass-border)]" data-testid="course-detail">
          <h2 className="text-lg font-semibold text-[var(--isp-heading)] mb-1">{courseDetail.course?.name}</h2>
          <p className="text-xs text-[var(--isp-muted)] mb-4">{courseDetail.enrolled_count} students · {courseDetail.materials_count} materials · Class avg: {courseDetail.class_average}%</p>

          {/* Grade Distribution */}
          {courseDetail.grade_distribution && (
            <div className="mb-4">
              <p className="text-xs font-medium text-[var(--isp-body)] mb-2">Grade Distribution</p>
              <div className="flex gap-2">
                {Object.entries(courseDetail.grade_distribution).map(([grade, count]) => {
                  const total = Object.values(courseDetail.grade_distribution).reduce((s, v) => s + v, 0) || 1;
                  const pct = Math.round((count / total) * 100);
                  return (
                    <div key={grade} className="flex-1 text-center">
                      <div className="h-16 flex items-end justify-center mb-1">
                        <div className={`w-full rounded-t ${GRADE_COLORS[grade]} opacity-60`} style={{ height: `${Math.max(pct, 8)}%` }} />
                      </div>
                      <p className="text-xs font-bold text-[var(--isp-heading)]">{grade}</p>
                      <p className="text-[10px] text-[var(--isp-muted)]">{count} ({pct}%)</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Student Performance Table */}
          {courseDetail.students?.length > 0 && (
            <div>
              <p className="text-xs font-medium text-[var(--isp-body)] mb-2">Student Performance</p>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {courseDetail.students.map((s, i) => (
                  <div key={s.id} className="flex items-center gap-2 p-2 rounded-lg bg-white/[0.02] hover:bg-white/[0.04]">
                    <span className="w-5 text-center text-[10px] text-[var(--isp-muted)]">#{i + 1}</span>
                    <span className="flex-1 text-xs text-[var(--isp-heading)] truncate">{s.name}</span>
                    <span className="text-xs text-[var(--isp-muted)]">{s.exams_taken} exams</span>
                    <span className={`text-xs font-bold ${s.avg_score >= 70 ? 'text-green-400' : s.avg_score >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>{s.avg_score}%</span>
                    <span className="text-xs text-purple-300">{s.xp} XP</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default EducatorAnalyticsPage;
